import React from 'react';
import styled from 'styled-components';
import { Body7 } from '@ui';
import { GhostButton } from '@ui/buttons';
import { GRID_GAP, ROW_PADDING } from './columns';
import { inputColumnLabel } from './format';
import { inputSortField, nextSort, SORT_DIRECTIONS, sortStateLabel } from './rowSort';
import { FrozenHeaderCell } from './cellStyles';

// The table's header: one sortable button per column, in the order the column model declares.
//
// Out of FeedRowsTable because it is one of the ten jobs that 607-line render body held, and
// because "restyle the header" and "change row density" were edits to the same function. Three
// groups, one rule each, and the rule is the same rule: label and sort field both come from the
// model, so a rename there cannot leave a header behind saying the old thing.

// The gutter and padding are interpolated from columns.js rather than retyped. tableGrid reserves
// the scroll container's min-width from those same numbers, so a literal here is a second source of
// truth for one measurement: tighten the gutter in this file alone and the container keeps
// reserving the old one, and the table scrolls sideways on a page it fits. That bug has shipped
// once already — see the note at columns.js:297-300.
const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  gap: ${GRID_GAP}px;
  padding: ${ROW_PADDING}px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.border};

  /* Painted so the header reads as a band rather than as another row. It no longer needs to be
     opaque for a sticky overlap — the table scrolls horizontally only — but the surface is a
     deliberate styling choice, so it stays. */
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
`;

// The company is the row's identity, so it stays readable however far right the markers run.
// A plain button, not a role="columnheader" with aria-sort: the grid is still divs, and a lone
// columnheader outside a table/row structure is invalid ARIA — worse than none. The state travels in
// the accessible name instead, until the real-table-semantics work lands.
const SortButton = styled(GhostButton)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const SortArrow = styled.span`
  font-size: 9px;
  line-height: 1;
  color: ${({ theme }) => theme.themeColors.primaryCtaButton};
`;

export const FeedTableHeader = ({ coreColumns, optionalColumns, memberColumns, gridColumns, sort, onSortChange }) => {
  const sortableHeader = (field, label, title) => {
    const isSorted = sort?.field === field;
    return (
      <SortButton
        onClick={() => onSortChange((prev) => nextSort(prev, field))}
        aria-label={sortStateLabel(sort, field, label)}
        title={title}
      >
        <Body7 bold>{label}</Body7>
        {isSorted && (
          <SortArrow aria-hidden="true">{sort.direction === SORT_DIRECTIONS.ASC ? '\u25B2' : '\u25BC'}</SortArrow>
        )}
      </SortButton>
    );
  };

  // Anchored for the column-order and gutter tests: the header's cell order and its grid tracks
  // come from one array now, and nothing else can see them agree.
  return (
    <HeaderRow $columns={gridColumns} data-testid="feed-header">
      {coreColumns.map((column) => (
        <React.Fragment key={column.key}>
          {column.frozen ? (
            <FrozenHeaderCell>{sortableHeader(column.sortField || column.key, column.label)}</FrozenHeaderCell>
          ) : (
            sortableHeader(column.sortField || column.key, column.label)
          )}
        </React.Fragment>
      ))}
      {optionalColumns.map((column) => (
        <React.Fragment key={column.key}>{sortableHeader(column.sortField, column.label)}</React.Fragment>
      ))}
      {memberColumns.map((column) => (
        <React.Fragment key={column.key}>
          {sortableHeader(inputSortField(column.key), inputColumnLabel(column), column.feedId)}
        </React.Fragment>
      ))}
    </HeaderRow>
  );
};
